import React from "react";
import { motion } from "framer-motion";
import {
  FaGithub,
  FaExternalLinkAlt,
  FaStar,
  FaCodeBranch,
} from "react-icons/fa";
import { HiOutlineExternalLink } from "react-icons/hi";
import { Link } from "react-router-dom";

const ProjectsHighlight = () => {
  const featuredProjects = [
    {
      title: "VXDF - Validated Exploitable Data Flow",
      description:
        "An open-source JSON standard for documenting confirmed security vulnerabilities with structured evidence. Eliminates false positives and improves security workflows.",
      tags: ["Open Source", "Security", "JSON", "Python"],
      github: "https://github.com/mihir-shah99/vxdf",
      demo: "https://vxdf.org",
      featured: true,
      gradient: "from-vxdf-primary to-vxdf-secondary",
      icon: "🛡️",
    },
    {
      title: "VulnDroid",
      description:
        "An Android application designed to demonstrate common mobile security vulnerabilities and their mitigations.",
      tags: ["Android", "Security", "Mobile", "Java"],
      github: "https://github.com/mihir-shah99/VulnDroid",
      gradient: "from-green-500 to-emerald-600",
      icon: "📱",
    },
    {
      title: "Learning Linux",
      description:
        "A comprehensive guide and resource collection for learning Linux system administration and security.",
      tags: ["Linux", "DevOps", "Security", "Documentation"],
      github: "https://github.com/mihir-shah99/Learning-Linux",
      gradient: "from-orange-500 to-red-600",
      icon: "🐧",
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-brand-primary to-brand-secondary bg-clip-text text-transparent">
            Featured Projects
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Showcasing key open-source contributions and security research
            projects
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {featuredProjects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className={`relative group ${project.featured ? "lg:col-span-2 lg:row-span-1" : ""}`}
            >
              <div
                className={`h-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border-2 ${
                  project.featured
                    ? "border-vxdf-primary"
                    : "border-transparent"
                } hover:border-brand-primary transition-all duration-300`}
              >
                {/* Gradient Header */}
                <div className={`h-2 bg-gradient-to-r ${project.gradient}`} />

                {/* Featured Badge */}
                {project.featured && (
                  <div className="absolute top-4 right-4 bg-vxdf-primary text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Leading Project
                  </div>
                )}

                <div className="p-8">
                  {/* Project Icon */}
                  <div className="text-5xl mb-4">{project.icon}</div>

                  {/* Project Title */}
                  <h3 className="text-2xl font-bold mb-3 text-gray-800 dark:text-white">
                    {project.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-300 mb-6 line-clamp-3">
                    {project.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.tags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Links */}
                  <div className="flex gap-4">
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-brand-primary dark:hover:text-brand-primary transition-colors"
                    >
                      <FaGithub className="text-xl" />
                      <span>View Code</span>
                    </a>
                    {project.demo && (
                      <a
                        href={project.demo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-brand-primary dark:hover:text-brand-primary transition-colors"
                      >
                        <FaExternalLinkAlt />
                        <span>Live Demo</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All Projects CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-primary to-brand-secondary text-white px-8 py-4 rounded-lg font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
          >
            View All Projects
            <HiOutlineExternalLink className="text-xl" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectsHighlight;
